import { createContext, useContext, useEffect, useState } from "react";
import primeClient from "../../axios/primeClient";
import { Card } from "../../Components/Card";
import {
  generateCircularProgressBarOption,
  PieChart,
} from "../../Components/PieChart";
import { useFetch } from "../../hooks/useFetch";
import { timeAgo } from "../../utils/timeAgo";
import { ISO8601t0DDMMYYYY } from "../../utils/parseDate";
import { Circle } from "../../Components/Circle";
import { loopRootCause, loopRootCauseID } from "../../utils/loopRootCause";
import Recommendations from "../../Components/Recommendations";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";
import { Colors } from "../../constants/Colors";

const DataContext = createContext();

export const PrimeAlarmDetails = ({ alarmId }) => {
  const {
    data: alarmData,
    isLoading,
    error,
  } = useFetch({
    url: `/v1/apps/alarm/detail?alarm_id=${alarmId}`,
    client: primeClient,
    initialData: {
      alarm_status: "",
      recent_status: "",
      recent_updated_at: "",
      data: {
        name: "",
        ip_address: "",
        last_update: "",
        component: [],
      },
    },
  });

  const { t, i18n } = useTranslation();

  return (
    <DataContext.Provider value={{ alarmData, isLoading, error }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <DeviceDescriptionCard />
        <Card
          style={{
            width: "100%",
            maxWidth: window.innerWidth - 30,
            padding: "1rem",
          }}
        >
          <ChartsCard />
          <br /> <hr /> <br />
          <Recommendations
            rootCause={
              isLoading || error
                ? []
                : i18n.language === "id"
                ? loopRootCauseID(alarmData.data).rootCause
                : loopRootCause(alarmData.data).rootCause
            }
          />
          <br /> <hr /> <br />
          <RecentConditionCard />
        </Card>
      </div>
    </DataContext.Provider>
  );
};

// prettier-ignore
const statusColors = {
  "up": Colors.alertsgreen100,
  "down": Colors.alertsred100,
  "attention": Colors.alertsyellow100,
}

// prettier-ignore
const levelColors = {
  "warning": Colors.alertsyellow80,
  "critical": Colors.alertsred80,
  "normal": Colors.alertsgreen80,
  "no_data": "#a7a9ac"
}

const DeviceDescriptionCard = () => {
  const { alarmData, isLoading, error } = useContext(DataContext);
  return (
    <Card
      style={{
        width: "100%",
        maxWidth: "400px",
        backgroundColor: Colors.primary100,
        color: Colors.white,
        padding: "0.5rem 1rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "bold",
        }}
      >
        <div>{isLoading || error ? "Device Name" : alarmData.data.name}</div>
        {!isLoading && !error && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Circle
              color={statusColors[alarmData.alarm_status]}
              diameter="1rem"
            />
            {alarmData.alarm_status.toUpperCase()}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          {isLoading || error ? "xxx.xxx.xxx.xxx" : alarmData.data.ip_address}
        </div>
        <div>
          {!isLoading &&
            !error &&
            ISO8601t0DDMMYYYY(alarmData.data.last_update)}
        </div>
      </div>
    </Card>
  );
};

const ChartsCard = () => {
  const { alarmData, isLoading, error } = useContext(DataContext);
  const [cpuData, setCpuData] = useState(null);
  const [memoryData, setMemoryData] = useState(null);
  const [storageData, setStorageData] = useState(null);

  useEffect(() => {
    if (!isLoading && !error) {
      const cpuData = alarmData.data.component
        .filter((el) => el.type === "cpu")
        .flatMap((el) => el.cores)
        .map((el) => {
          return {
            chart: generateCircularProgressBarOption(
              el.level === "no_data" ? "N/A" : el.value + "%",
              null,
              levelColors[el.level],
              el.level === "no_data" ? 0 : el.value
            ),
            name: el.core,
          };
        });

      setCpuData(cpuData);

      const memoryData = alarmData.data.component
        .filter((el) => el.type === "memory")
        .flatMap((el) => el.indexes)
        .map((el) => {
          return {
            chart: generateCircularProgressBarOption(
              el.level === "no_data" ? "N/A" : el.value + "%",
              null,
              levelColors[el.level],
              el.level === "no_data" ? 0 : el.value
            ),
            name: el.core,
          };
        });

      setMemoryData(memoryData);

      const storageData = alarmData.data.component
        .filter((el) => el.type === "storage")
        .flatMap((el) => el.partitions)
        .map((el) => {
          return {
            chart: generateCircularProgressBarOption(
              el.level === "no_data" ? "N/A" : el.value + "%",
              null,
              levelColors[el.level],
              el.level === "no_data" ? 0 : el.value
            ),
            name: el.core,
          };
        });

      setStorageData(storageData);
    }
  }, [alarmData, isLoading, error]);

  return (
    <div
      style={{
        padding: "0.5rem 1rem",
        height: "fit-content",
        display: "flex",
        gap: "0.5rem",
        width: "100%",
      }}
    >
      <div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            backgroundColor: Colors.neutral10,
            fontSize: "0.8rem",
            padding: "0.5rem",
            borderTopLeftRadius: "5px",
          }}
        >
          CPU
        </div>
        {cpuData && (
          <div style={{ display: "flex", overflowY: "auto", width: "180px" }}>
            {cpuData.map((el, idx) => (
              <div key={`cpu-${idx}`}>
                <PieChart options={el.chart} chartWidth={60} chartHeight={60} />
                <div style={{ textAlign: "center" }}>
                  {idx}{" "}
                  <InformationCircleIcon
                    color="darkgrey"
                    width={"1rem"}
                    style={{
                      display: "inline",
                      cursor: "pointer",
                    }}
                    data-tooltip-id={`cpu-${idx}-tooltip`}
                  />
                  <Tooltip
                    id={`cpu-${idx}-tooltip`}
                    place="bottom"
                    style={{
                      fontSize: "0.7rem",
                      maxWidth: "200px",
                    }}
                    clickable
                  >
                    <div style={{ overflow: "auto" }}>{el.name}</div>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            backgroundColor: Colors.neutral10,
            fontSize: "0.8rem",
            padding: "0.5rem",
          }}
        >
          Memory
        </div>
        {memoryData && (
          <div style={{ display: "flex", width: "180px", overflowY: "auto" }}>
            {memoryData.map((el, idx) => (
              <div key={`memory-${idx}`}>
                <PieChart options={el.chart} chartWidth={60} chartHeight={60} />
                <div style={{ textAlign: "center" }}>
                  {idx}{" "}
                  <InformationCircleIcon
                    color="darkgrey"
                    width={"1rem"}
                    style={{ display: "inline", cursor: "pointer" }}
                    data-tooltip-id={`memory-${idx}-tooltip`}
                  />
                  <Tooltip
                    id={`memory-${idx}-tooltip`}
                    place="bottom"
                    style={{
                      fontSize: "0.7rem",
                      maxWidth: "200px",
                    }}
                    clickable
                  >
                    <div style={{ overflow: "auto" }}>{el.name}</div>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            backgroundColor: Colors.neutral10,
            fontSize: "0.8rem",
            padding: "0.5rem",
            borderTopRightRadius: "5px",
          }}
        >
          Storage
        </div>
        {storageData && (
          <div style={{ display: "flex", width: "180px", overflowY: "auto" }}>
            {storageData.map((el, idx) => (
              <div key={`storage-${idx}`}>
                <PieChart options={el.chart} chartWidth={60} chartHeight={60} />
                <div style={{ textAlign: "center" }}>
                  {idx}{" "}
                  <InformationCircleIcon
                    color="darkgrey"
                    width={"1rem"}
                    style={{ display: "inline", cursor: "pointer" }}
                    data-tooltip-id={`storage-${idx}-tooltip`}
                  />
                  <Tooltip
                    id={`storage-${idx}-tooltip`}
                    place="bottom"
                    style={{
                      fontSize: "0.7rem",
                      maxWidth: "200px",
                    }}
                    clickable
                  >
                    <div style={{ overflow: "auto" }}>{el.name}</div>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RecentConditionCard = () => {
  const { alarmData, isLoading, error } = useContext(DataContext);
  const { t } = useTranslation();
  return (
    <div style={{}}>
      <div
        style={{
          marginBottom: "5px",
          fontWeight: "bold",
        }}
      >
        {t("prime.alarm.recentCondition")}
      </div>
      <div
        style={{
          backgroundColor: Colors.neutral10,
          padding: "0.5rem",
          borderRadius: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <div>{t("prime.alarm.recentConditionText")} </div>
          {!isLoading && !error && (
            <div
              style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
            >
              <Circle
                color={statusColors[alarmData.recent_status]}
                diameter={"1rem"}
              />
              <strong>{alarmData.recent_status.toUpperCase()}</strong>
            </div>
          )}
        </div>

        <div>{timeAgo(alarmData.recent_updated_at, t)}</div>
      </div>
    </div>
  );
};
